// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { wrapApiError, wrapCaughtError, ApiError } from '@/utils/errors/apiErrorWrapper';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PostCollectionProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  body?: any;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Posts a collection for a workspace
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param body Optional request body
 * @returns Promise with API response or error
 */
export const postCollection = async ({
  getAccessToken,
  workspaceId,
  body,
}: PostCollectionProps): Promise<any> => {
  try {
    console.log('🔄 Posting collection...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/collection`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      // Use comprehensive error extraction to get all error details
      throw await wrapApiError(response, convertURI, 'POST');
    }

    const data = await response.json();
    console.log(`✅ Collection posted successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error posting collection:', error);

    // If it's already an ApiError, re-throw it (it has all the info)
    if (error instanceof ApiError) {
      throw error;
    }

    // Otherwise, wrap it to extract all available information
    if (error instanceof Error) {
      const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/collection`;
      throw await wrapCaughtError(error, body, encodeURI(url), 'POST');
    }

    throw error;
  }
};
